import React, { useState, useEffect } from 'react';
import { Download, Printer, Building2, Search, AlertTriangle, FileText } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { exportToPDF } from '@/utils/groupReportPDF';

const GroupDueFeeReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedCollege, setSelectedCollege] = useState('');
  const [colleges, setColleges] = useState([]);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => { fetchColleges(); fetchReport(); }, []);

  const fetchColleges = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/groups/my-group`, { headers: { Authorization: `Bearer ${token}` } });
      setColleges(res.data.colleges || []);
    } catch (e) { console.error(e); }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/api/groups/my-group/reports/due-fees?`;
      if (selectedCollege) url += `college_id=${selectedCollege}&`;
      if (fromDate) url += `from_date=${fromDate}&`;
      if (toDate) url += `to_date=${toDate}&`;
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch (e) { toast.error('Failed to load report'); }
    finally { setLoading(false); }
  };

  const exportToExcel = () => {
    if (!data?.all_students?.length) return toast.error('No data to export');
    const ws = XLSX.utils.json_to_sheet(data.all_students.map(s => ({
      'College': s.college_name, 'Student': s.full_name, 'Admission No': s.admission_number,
      'Course': s.course_name, 'Session': s.session, 'Total Fee': s.total_fee, 'Paid': s.paid, 'Due': s.due, 'Phone': s.phone
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Due Fee Report');
    XLSX.writeFile(wb, `Due_Fee_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const exportPDF = () => {
    if (!data?.all_students?.length) return toast.error('No data to export');
    exportToPDF('Due Fee Report',
      ['College', 'Student', 'Adm No', 'Course', 'Session', 'Total Fee', 'Paid', 'Due', 'Phone'],
      data.all_students.map(s => [s.college_name, s.full_name, s.admission_number, s.course_name, s.session, `₹${(s.total_fee||0).toLocaleString('en-IN')}`, `₹${(s.paid||0).toLocaleString('en-IN')}`, `₹${(s.due||0).toLocaleString('en-IN')}`, s.phone]),
      `Due_Fee_Report_${new Date().toISOString().split('T')[0]}`,
      `Total Due: ₹${(data.totals?.total_due||0).toLocaleString('en-IN')} | Students: ${data.totals?.total_students_with_due||0}`
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="group-due-fee-report">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Due Fee Report</h1>
          <p className="text-sm text-gray-500">Pending fees across all colleges</p>
        </div>
        <div className="flex gap-2">
          <button onClick={exportToExcel} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"><Download className="h-4 w-4" /> Excel</button>
          <button onClick={exportPDF} className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"><FileText className="h-4 w-4" /> PDF</button>
          <button onClick={() => window.print()} className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm"><Printer className="h-4 w-4" /> Print</button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow p-4 mb-6 flex flex-wrap gap-4 items-end">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Admission From</label>
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="border rounded-lg px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Admission To</label>
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="border rounded-lg px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">College</label>
          <select value={selectedCollege} onChange={e => setSelectedCollege(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
            <option value="">All Colleges</option>
            {colleges.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <button onClick={fetchReport} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm flex items-center gap-2"><Search className="h-4 w-4" /> Search</button>
      </div>

      {loading ? <div className="text-center py-12 text-gray-500">Loading...</div> : data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-white rounded-xl shadow p-5 border-l-4 border-red-500">
              <p className="text-xs text-gray-500">Total Due Amount</p>
              <p className="text-3xl font-bold text-red-600">₹{(data.totals?.total_due || 0).toLocaleString('en-IN')}</p>
            </div>
            <div className="bg-white rounded-xl shadow p-5 border-l-4 border-orange-500">
              <p className="text-xs text-gray-500">Students with Due</p>
              <p className="text-3xl font-bold text-orange-600">{data.totals?.total_students_with_due || 0}</p>
            </div>
          </div>

          {data.colleges?.map(college => (
            <div key={college.college_id} className="bg-white rounded-xl shadow mb-4">
              <div className="p-4 border-b flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-blue-600" />
                  <h3 className="font-bold text-gray-900">{college.college_name}</h3>
                  <span className="text-xs text-gray-500">({college.students_with_due} students)</span>
                </div>
                <span className="text-lg font-bold text-red-600">₹{(college.total_due || 0).toLocaleString('en-IN')}</span>
              </div>
              {college.students?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left p-3">Student</th>
                        <th className="text-left p-3">Admission No</th>
                        <th className="text-left p-3">Course</th>
                        <th className="text-left p-3">Session</th>
                        <th className="text-right p-3">Total Fee</th>
                        <th className="text-right p-3">Paid</th>
                        <th className="text-right p-3">Due</th>
                        <th className="text-left p-3">Phone</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {college.students.map((s, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="p-3 font-medium">{s.full_name}</td>
                          <td className="p-3 font-mono text-xs">{s.admission_number}</td>
                          <td className="p-3">{s.course_name}</td>
                          <td className="p-3">{s.session}</td>
                          <td className="p-3 text-right">₹{(s.total_fee || 0).toLocaleString('en-IN')}</td>
                          <td className="p-3 text-right text-green-700">₹{(s.paid || 0).toLocaleString('en-IN')}</td>
                          <td className="p-3 text-right font-bold text-red-600">₹{(s.due || 0).toLocaleString('en-IN')}</td>
                          <td className="p-3">{s.phone}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <p className="p-4 text-gray-400 text-sm">No dues found</p>}
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default GroupDueFeeReport;
